import { useContext, useState } from "react";
import { useAuthQuery as useQuery } from '../../shared/hooks/useAuthQuery';
import { useMutation } from "convex/react";
import { useNavigate } from "react-router-dom";
import { api } from "../../convex/_generated/api";
import { parseMarkdown } from "../../shared/helpers/markdown";
import { BaseContext } from "../../shared/base-context";
import { Id } from "../../convex/_generated/dataModel";
import { Button } from "../../catalyst-components/button";
import { Dialog, DialogTitle, DialogDescription, DialogActions } from "../../catalyst-components/dialog";
import { usePersistedForm } from "../../shared/hooks/usePersistedForm";
import { AppGuideTab } from "./components/AppGuideTab";

export default function DocumentationPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<number | 'app_guide'>('app_guide');
  const [isEditing, setIsEditing] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [sectionToDelete, setSectionToDelete] = useState<Id<"documentation_sections"> | null>(null);

  const [formState, updateFormState] = usePersistedForm('documentation-form', {
    editContent: "",
    newTitle: "",
    newContent: ""
  });

  const sections = useQuery(api.documentation.getDocumentationSections) || [];
  const updateSection = useMutation(api.documentation.updateDocumentationSection);
  const createSection = useMutation(api.documentation.createDocumentationSection);
  const deleteSection = useMutation(api.documentation.deleteDocumentationSection);
  const {convexUserId} = useContext(BaseContext);

  const handleEdit = (content: string) => {
    updateFormState({ ...formState, editContent: content });
    setIsEditing(true);
  };

  const handleSave = async () => {
    if (sections[activeTab]) {
      await updateSection({
        id: sections[activeTab]._id,
        title: sections[activeTab].title,
        content: formState.editContent,
        sequence: sections[activeTab].sequence,
        user_id: convexUserId as Id<"users">,
      });
      setIsEditing(false);
    }
  };

  const handleCreate = async () => {
    if (formState.newTitle && formState.newContent) {
      await createSection({
        title: formState.newTitle,
        content: formState.newContent,
        sequence: sections.length,
        user_id: convexUserId as Id<"users">,
      });
      setIsCreating(false);
      updateFormState({ ...formState, newTitle: "", newContent: "" });
    }
  };

  const handleDelete = async () => {
    if (sectionToDelete) {
      await deleteSection({ id: sectionToDelete });
      setIsDeleteDialogOpen(false);
      setSectionToDelete(null);
      if (activeTab >= sections.length - 1) {
        setActiveTab(Math.max(0, sections.length - 2));
      }
    }
  };

  const isAppGuide = activeTab === 'app_guide';

  if (!sections.length && !isCreating && !isAppGuide) {
    // If no sections exist but we're not on App Guide, switch to it
    setActiveTab('app_guide');
  }

  return (
    <div className="min-h-screen bg-background pt-24 p-4">
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-4">
          <Button color="light" onClick={() => navigate('../')}>
            Go back
          </Button>
          <h1 className="text-2xl font-bold">Documentation</h1>
        </div>
        {!isCreating && !isAppGuide && (
          <Button color="light" onClick={() => setIsCreating(true)}>
            Add New Section
          </Button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex border-b mb-4">
        <button
          className={`px-4 py-2 font-medium ${
            isAppGuide
              ? "border-b-2 border-primary-5 text-primary-5"
              : "text-gray-500 hover:text-gray-700"
          }`}
          onClick={() => setActiveTab('app_guide')}
        >
          App Guide
        </button>
        {sections.map((section, index) => (
          <button
            key={section._id.toString()}
            className={`px-4 py-2 ${
              activeTab === index
                ? "border-b-2 border-primary-5 text-primary-5"
                : "text-gray-500 hover:text-gray-700"
            }`}
            onClick={() => setActiveTab(index)}
          >
            {section.title}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {isAppGuide ? (
        <AppGuideTab />
      ) : isCreating ? (
        <div className="space-y-4">
          <input
            type="text"
            placeholder="Section Title"
            className="w-full p-2 border rounded"
            value={formState.newTitle}
            onChange={(e) => updateFormState({ ...formState, newTitle: e.target.value })}
          />
          <textarea
            className="w-full h-96 p-4 border rounded"
            placeholder="Section Content (Markdown)"
            value={formState.newContent}
            onChange={(e) => updateFormState({ ...formState, newContent: e.target.value })}
          />
          <div className="flex space-x-2">
            <Button color="light" onClick={handleCreate}>
              Create
            </Button>
            <Button color="light" onClick={() => setIsCreating(false)}>
              Cancel
            </Button>
          </div>
        </div>
      ) : typeof activeTab === 'number' && sections[activeTab] ? (
        <div className="relative">
          {isEditing ? (
            <div className="space-y-4">
              <textarea
                className="w-full h-96 p-4 border rounded"
                value={formState.editContent}
                onChange={(e) => updateFormState({ ...formState, editContent: e.target.value })}
              />
              <div className="flex space-x-2">
                <Button color="light" onClick={handleSave}>
                  Save
                </Button>
                <Button color="light" onClick={() => setIsEditing(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <div className="relative">
              <div className="absolute top-0 right-0 flex space-x-2">
                <Button
                  color="light"
                  onClick={() => handleEdit(sections[activeTab].content)}
                >
                  Edit
                </Button>
                <Button
                  color="red"
                  onClick={() => {
                    setSectionToDelete(sections[activeTab]._id);
                    setIsDeleteDialogOpen(true);
                  }}
                >
                  Delete
                </Button>
              </div>
              <div
                className="prose max-w-none"
                dangerouslySetInnerHTML={{
                  __html: parseMarkdown(sections[activeTab].content, ["###", "##", "#"]),
                }}
              />
            </div>
          )}
        </div>
      ) : !isAppGuide ? (
        <p className="text-gray-500">No documentation sections available.</p>
      ) : null}

      <Dialog
        open={isDeleteDialogOpen}
        onClose={() => {
          setIsDeleteDialogOpen(false);
          setSectionToDelete(null);
        }}
      >
        <DialogTitle>Delete Section</DialogTitle>
        <DialogDescription>
          Are you sure you want to delete this section? This action cannot be undone.
        </DialogDescription>
        <DialogActions>
          <Button
            color="light"
            onClick={() => {
              setIsDeleteDialogOpen(false);
              setSectionToDelete(null);
            }}
          >
            Cancel
          </Button>
          <Button color="red" onClick={handleDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
} 